import React from 'react';
import { Chip, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import { useTranslation } from 'react-i18next';
import { AnimalPhotosService } from '../../hooks/AnimalPhotosService';
import { extractApiErrorMessage, notifications } from '../../utils';
import AnimalPhotoGallery from './AnimalPhotoGallery';

export interface AnimalPhotosIndicatorProps {
  animalId: string;
}

/**
 * A chip beside the animal's other header chips, shown only when the animal has photos. Photos are
 * taken in the mobile app during register-by-injection. The page asks only for the count; the gallery,
 * and the image links it needs, load when the dialog opens.
 */
const AnimalPhotosIndicator: React.FC<AnimalPhotosIndicatorProps> = ({ animalId }) => {
  const { t } = useTranslation();
  const [count, setCount] = React.useState(0);
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    const service = new AnimalPhotosService();
    // The page can move to another animal while a request is in flight.
    let cancelled = false;

    setCount(0);
    setIsOpen(false);

    service
      .getPhotoCount(animalId)
      .then((result) => {
        if (cancelled) return;
        setCount(result);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });

    return () => {
      cancelled = true;
    };
  }, [animalId]);

  if (count === 0) {
    return null;
  }

  return (
    <>
      <Chip
        id="animal-photos-chip"
        sx={{ m: 1 }}
        icon={<PhotoCameraOutlinedIcon />}
        label={t('f-animal-photos-count', { count, defaultValue: 'Photos: {{count}}' })}
        color="primary"
        size="small"
        variant="outlined"
        onClick={() => setIsOpen(true)}
      />

      <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle className="flex items-center justify-between gap-3 !pr-3">
          <span>{t('f-animal-photos-title', { defaultValue: 'Photos' })}</span>
          <IconButton aria-label={t('f-com-close')} onClick={() => setIsOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <AnimalPhotoGallery animalId={animalId} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AnimalPhotosIndicator;
